import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { loadLocale } from '@/i18n';

interface AppHeaderProps {
  /** Show breadcrumb in detail mode */
  currentPage?: 'list' | 'detail';
  projectName?: string;
  onLogout?: () => void;
}

/**
 * Top application header matching the UX design:
 * - Logo + app name (clickable, navigates home)
 * - Optional breadcrumb when on detail page
 * - EN / CS language buttons (both always visible)
 * - Username + avatar with dropdown (logout)
 */
export const AppHeader = ({ currentPage, projectName, onLogout }: AppHeaderProps): React.ReactElement => {
  const auth = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { i18n } = useLingui();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = (): void => {
    setShowUserMenu(false);
    if (onLogout) onLogout();
  };

  // Derive user display name and initials from OIDC profile
  const userProfile = auth.user?.profile;
  const userName: string =
    (userProfile?.name as string | undefined) ??
    (userProfile?.preferred_username as string | undefined) ??
    (userProfile?.email as string | undefined) ??
    'User';
  const initials = userName
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="sticky top-0 z-50 flex h-14 items-center gap-2 sm:gap-4 border-b border-slate-200 bg-white px-2 sm:px-6 shadow-sm">
      {/* Logo + app name – click navigates to project list */}
      <Link to="/projects" className="flex select-none items-center gap-2">
        <img src="/kasko-logo.svg" alt="Kasko" className="h-6 w-auto" />
        <span className="hidden text-xl font-light tracking-tight text-slate-600 sm:inline">
          <Trans>Collision</Trans>
        </span>
      </Link>

      {/* Breadcrumb – only on detail page */}
      {currentPage === 'detail' && (
        <nav className="flex min-w-0 items-center gap-1.5 text-slate-500" aria-label="Breadcrumb">
          <Link to="/projects" className="shrink-0 text-blue-600 hover:underline">
            <Trans>Projects</Trans>
          </Link>
          <span className="shrink-0 text-slate-500">›</span>
          <span className="max-w-[80px] truncate font-medium text-slate-800 sm:max-w-[120px] md:max-w-[200px] lg:max-w-none">
            {projectName}
          </span>
        </nav>
      )}

      <div className="flex-1" />

      {/* Language toggle – both EN and CS always visible */}
      <ToggleGroup
        variant="outline"
        size="sm"
        value={[i18n.locale]}
        onValueChange={(value) => value[0] && loadLocale(value[0] as 'en' | 'cs')}
      >
        <ToggleGroupItem value="en" aria-label="English">
          EN
        </ToggleGroupItem>
        <ToggleGroupItem value="cs" aria-label="Czech">
          CS
        </ToggleGroupItem>
      </ToggleGroup>

      {/* User avatar + dropdown */}
      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setShowUserMenu((v) => !v)}
          className="flex cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent"
          aria-label="User menu"
        >
          <span className="hidden font-medium text-slate-600 md:inline">{userName}</span>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-600">
            {initials}
          </div>
        </button>

        {showUserMenu && (
          <div className="fade-in card absolute right-0 top-full mt-1 min-w-44 p-1.5 shadow-lg">
            <div className="border-b border-slate-100 px-3 py-2">
              <div className="font-semibold">{userName}</div>
            </div>
            {onLogout && (
              <Button type="button" variant="ghost" onClick={handleLogout} className="w-full justify-start">
                <Trans>Log out</Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
